import { useState, useEffect } from 'react';
import { getSupabaseUrl, getSupabaseAnonKey } from '../../lib/environments';
import { getAccessToken } from '../../lib/supabase';

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface Circle {
  id: string;
  name: string;
  member_count: number;
}

interface Campaign {
  id: string;
  name: string;
  title: string;
  body: string;
  target_audience: string;
  recipient_count: number;
  status: string;
  sent_at: string | null;
  created_at: string;
  metadata: any;
}

type TargetType = 'all' | 'circle' | 'individuals';

export function SystemNotificationsManager() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState<TargetType>('all');
  const [selectedCircle, setSelectedCircle] = useState<Circle | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingCircles, setLoadingCircles] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load past campaigns on mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Load circles when target changes to circle
  useEffect(() => {
    if (target === 'circle' && circles.length === 0) {
      fetchCircles();
    }
  }, [target]);

  // Debounced user search
  useEffect(() => {
    if (target !== 'individuals' || userSearch.length < 2) {
      setUserSearchResults([]);
      return;
    }

    const timer = setTimeout(() => searchUsers(userSearch), 300);
    return () => clearTimeout(timer);
  }, [userSearch, target]);

  async function apiCall(action?: string, params?: Record<string, string>, body?: any) {
    const token = getAccessToken();
    const url = new URL(`${getSupabaseUrl()}/functions/v1/admin-notifications-api`);
    if (action) url.searchParams.set('action', action);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }

    const res = await fetch(url.toString(), {
      method: body ? 'POST' : 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'apikey': getSupabaseAnonKey(),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    return res.json();
  }

  async function fetchCampaigns() {
    const data = await apiCall();
    if (data.success) setCampaigns(data.campaigns || []);
  }

  async function fetchCircles() {
    setLoadingCircles(true);
    const data = await apiCall('circles');
    if (data.success) setCircles(data.circles || []);
    setLoadingCircles(false);
  }

  async function searchUsers(query: string) {
    setSearchingUsers(true);
    const data = await apiCall('users', { search: query });
    if (data.success) {
      // Filter out already selected users
      const selectedIds = new Set(selectedUsers.map(u => u.id));
      setUserSearchResults((data.users || []).filter((u: User) => !selectedIds.has(u.id)));
    }
    setSearchingUsers(false);
  }

  async function handleSend() {
    if (!title.trim() || !message.trim()) {
      setResult({ type: 'error', message: 'Title and message are required.' });
      return;
    }
    if (target === 'circle' && !selectedCircle) {
      setResult({ type: 'error', message: 'Please select a circle.' });
      return;
    }
    if (target === 'individuals' && selectedUsers.length === 0) {
      setResult({ type: 'error', message: 'Please select at least one user.' });
      return;
    }

    const recipientDesc =
      target === 'all' ? 'ALL users' :
      target === 'circle' ? `circle "${selectedCircle!.name}" (${selectedCircle!.member_count} members)` :
      `${selectedUsers.length} selected user${selectedUsers.length !== 1 ? 's' : ''}`;

    if (!window.confirm(`Send notification to ${recipientDesc}?\n\nTitle: ${title}\nMessage: ${message}`)) {
      return;
    }

    setSending(true);
    setResult(null);

    const data = await apiCall(undefined, undefined, {
      title: title.trim(),
      message: message.trim(),
      target,
      circleId: selectedCircle?.id,
      userIds: selectedUsers.map(u => u.id),
    });

    setSending(false);

    if (data.success) {
      setResult({ type: 'success', message: data.message });
      setTitle('');
      setMessage('');
      setSelectedCircle(null);
      setSelectedUsers([]);
      setUserSearch('');
      fetchCampaigns();
    } else {
      setResult({ type: 'error', message: data.error || 'Failed to send notification.' });
    }
  }

  function addUser(user: User) {
    setSelectedUsers(prev => [...prev, user]);
    setUserSearchResults(prev => prev.filter(u => u.id !== user.id));
    setUserSearch('');
  }

  function removeUser(userId: string) {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  }

  function getTargetLabel(campaign: Campaign): string {
    if (campaign.target_audience === 'all') return 'All Users';
    if (campaign.target_audience === 'circle') return 'Circle';
    if (campaign.target_audience === 'individuals') return 'Individuals';
    return campaign.target_audience;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-theme-primary mb-6">System Notifications</h1>

      {/* Compose */}
      <div className="bg-surface rounded-lg border border-default p-6 mb-8">
        <h2 className="text-lg font-semibold text-theme-primary mb-4">Send Notification</h2>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Notification title"
              className="w-full px-3 py-2 border border-default rounded-md bg-surface text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={255}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-1">Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Notification message"
              rows={3}
              className="w-full px-3 py-2 border border-default rounded-md bg-surface text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
            />
          </div>

          {/* Target */}
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Recipients</label>
            <div className="flex gap-2">
              {(['all', 'circle', 'individuals'] as TargetType[]).map(t => (
                <button
                  key={t}
                  onClick={() => { setTarget(t); setResult(null); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    target === t
                      ? 'bg-blue-600 text-white'
                      : 'bg-surface border border-default text-theme-secondary hover:bg-primary-light'
                  }`}
                >
                  {t === 'all' ? 'Everyone' : t === 'circle' ? 'By Circle' : 'Select Users'}
                </button>
              ))}
            </div>
          </div>

          {/* Circle picker */}
          {target === 'circle' && (
            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-1">Circle</label>
              {loadingCircles ? (
                <p className="text-sm text-theme-muted">Loading circles...</p>
              ) : (
                <select
                  value={selectedCircle?.id || ''}
                  onChange={e => {
                    const circle = circles.find(c => c.id === e.target.value);
                    setSelectedCircle(circle || null);
                  }}
                  className="w-full px-3 py-2 border border-default rounded-md bg-surface text-theme-primary"
                >
                  <option value="">Select a circle...</option>
                  {circles.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.member_count} member{c.member_count !== 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Individual user picker */}
          {target === 'individuals' && (
            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-1">Search Users</label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 border border-default rounded-md bg-surface text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchingUsers && (
                  <div className="absolute right-3 top-2.5">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Search results dropdown */}
              {userSearchResults.length > 0 && (
                <div className="mt-1 border border-default rounded-md bg-surface shadow-lg max-h-48 overflow-y-auto">
                  {userSearchResults.map(user => (
                    <button
                      key={user.id}
                      onClick={() => addUser(user)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-primary-light text-left transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium overflow-hidden flex-shrink-0">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          (user.full_name?.[0] || user.email?.[0] || '?').toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-theme-primary truncate">{user.full_name || 'No name'}</div>
                        <div className="text-xs text-theme-muted truncate">{user.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected users chips */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedUsers.map(user => (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {user.full_name || user.email}
                      <button
                        onClick={() => removeUser(user.id)}
                        className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 font-bold"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Result banner */}
          {result && (
            <div className={`p-3 rounded-md text-sm ${
              result.type === 'success'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
            }`}>
              {result.message}
            </div>
          )}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>

      {/* Past campaigns */}
      <div className="bg-surface rounded-lg border border-default p-6">
        <h2 className="text-lg font-semibold text-theme-primary mb-4">Past Notifications</h2>

        {campaigns.length === 0 ? (
          <p className="text-sm text-theme-muted">No notifications sent yet.</p>
        ) : (
          <div className="space-y-3">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="border border-default rounded-md p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-theme-primary">{campaign.title}</div>
                    <div className="text-sm text-theme-secondary mt-1">{campaign.body}</div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-theme-muted">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                        {getTargetLabel(campaign)}
                      </span>
                      <span>{campaign.recipient_count} recipient{campaign.recipient_count !== 1 ? 's' : ''}</span>
                      <span>
                        {campaign.sent_at
                          ? new Date(campaign.sent_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: 'numeric', minute: '2-digit',
                            })
                          : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    campaign.status === 'sent'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    {campaign.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
